import { supabase } from "@/integrations/supabase/client";
import { setAccessToken } from "@/lib/db/auth-store";
import { updateUserProfile, uploadProfileAvatar } from "@/services";
import type { Profile } from "@/types/profile";
import { cleanupAuthState, performRobustSignOut } from "@/utils/authUtils";
import type { Session, User } from "@supabase/supabase-js";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from '@/components/ui/sonner';
import { getErrorMessage } from "@/utils/serviceResponse";
import { AuthContext, AuthContextType } from "./AuthContextDefinition";
import { logger } from "@/lib/logger";

const DEFAULT_APP_ORIGIN = 'http://localhost:3000';

const getAppOrigin = () => {
  // In browser, always use current origin so dev servers / previews work
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  const configuredAppUrl = import.meta.env.VITE_APP_URL?.trim();
  if (configuredAppUrl) {
    return configuredAppUrl.replace(/\/+$/, '');
  }

  return DEFAULT_APP_ORIGIN;
};

const buildAppUrl = (path: string) => new URL(path, `${getAppOrigin()}/`).toString();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string, retryCount = 0) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        // Retry up to 3 times with increasing delay instead of attempting
        // session recovery (which could inadvertently sign the user out).
        if (retryCount < 3) {
          const delay = 1000 * (retryCount + 1); // 1s, 2s, 3s
          logger.warn(`Profile fetch failed (attempt ${retryCount + 1}/3), retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          return fetchProfile(userId, retryCount + 1);
        }
        
        logger.error("Error fetching profile after retries:", { error: error });
        return;
      }

      setProfile(data);
    } catch (error) {
      logger.error("Error in fetchProfile:", { error: error });
    }
  }, []);

  // Refresh profile data
  const refreshProfile = async () => {
    await fetchProfile(user?.id || '');
  };

  // Fetch profile when user changes
  useEffect(() => {
    if (user) {
      // Use setTimeout to defer fetchProfile to the next event loop tick.
      // This prevents recursive Supabase auth calls that can occur if fetchProfile triggers
      // another auth state change, avoiding infinite loops or stack overflows.
      setTimeout(() => {
        fetchProfile(user.id);
      }, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchProfile is intentionally stable-less here to avoid auth/profile recursion
  }, [user]);

  // Listen for auth changes
  useEffect(() => {
    let isMounted = true;

    // Use onAuthStateChange as the single source of truth for session state.
    // The INITIAL_SESSION event fires after the SDK finishes initialization
    // (including detecting OAuth tokens in the URL hash), which avoids the
    // race condition where getSession() returns null before tokens are processed.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) {return;}

        logger.info('Auth state changed', { context: { event } });

        if (session) {
          setSession(session);
          setUser(session.user);
          // Feed the token store used by collection write-through handlers
          // and Electric shape Authorization headers (fires on sign-in,
          // INITIAL_SESSION, and TOKEN_REFRESHED).
          setAccessToken(session.access_token);
          // Profile fetch is handled by the user effect — no need to call it here.
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          setAccessToken(null);
        }
        
        setLoading(false);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Sign up with email and password
  const signUp = async (
    email: string,
    password: string,
    metadata?: Record<string, unknown>,
    captchaToken?: string
  ) => {
    try {
      // Clean up existing state first
      cleanupAuthState();
      
      const emailRedirectTo = buildAppUrl('/auth/confirm');

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo,
          captchaToken,
        }
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      toast.success("Verification email sent! Please check your inbox.");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign up";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken,
        }
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign in";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Sign in / Sign up with Google
  const signInWithGoogle = async (options?: { scopes?: string; redirectTo?: string }) => {
    try {
      const redirectTo = options?.redirectTo || buildAppUrl('/auth?redirect=%2Fdashboard');
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
          // Standard identity scopes by default so any personal or workspace Google user can sign in/up cleanly.
          scopes: options?.scopes || 'openid email profile',
        },
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      // Show loading message for OAuth flow
      toast.info("Redirecting to Google...");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign in with Google";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Enhanced sign out with better error handling
  const signOut = async () => {
    try {
      // Starting sign out process
      
      // Show immediate feedback to user
      toast.info("Signing out...");
      
      // Immediately update local state to provide instant UI feedback
      setSession(null);
      setUser(null);
      setProfile(null);
      
      // Perform robust sign out in background
      await performRobustSignOut();
      
      // Force page reload for a completely clean state
      // Redirecting to auth page
      setTimeout(() => {
        window.location.href = '/auth';
      }, 500); // Small delay to let the toast show
      
    } catch (error: unknown) {
      logger.error('Sign out error:', { error: error });
      
      // Even if sign out fails, ensure local state is cleared
      setSession(null);
      setUser(null);
      setProfile(null);
      
      // Show appropriate error message
      const errorMessage = error instanceof Error ? error.message : "Failed to sign out";
      
      // Check if it's a network/ad blocker issue
      if (errorMessage.includes('ERR_BLOCKED_BY_ADBLOCKER') || 
          errorMessage.includes('net::') ||
          errorMessage.includes('Auth session missing')) {
        toast.success("Signed out locally (network request blocked by ad blocker)");
      } else {
        toast.error(`Sign out error: ${errorMessage}`);
      }
      
      // Still redirect to clear the UI state
      setTimeout(() => {
        window.location.href = '/auth';
      }, 1000);
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      const redirectTo = buildAppUrl('/auth/reset-password');
        
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }

      toast.success("Password reset email sent! Please check your inbox.");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send reset email";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update profile
  const updateProfile = async (profileData: Partial<Profile>): Promise<boolean> => {
    if (!user) {
      toast.error("You must be logged in to update your profile");
      return false;
    }

    try {
      const result = await updateUserProfile(user.id, profileData);
      
      if (!result.success) {
        toast.error(getErrorMessage(result.error) || "Failed to update profile");
        return false;
      }
      
      // Refresh profile from database to get the updated data
      await refreshProfile();
      toast.success("Profile updated successfully");
      return true;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update profile";
      toast.error(errorMessage);
      return false;
    }
  };

  // Upload avatar
  const uploadAvatar = async (file: File): Promise<string | null> => {
    if (!user) {
      toast.error("You must be logged in to upload an avatar");
      return null;
    }

    try {
      const result = await uploadProfileAvatar(user.id, file);
      
      if (!result.success) {
        toast.error(getErrorMessage(result.error) || "Failed to upload avatar");
        return null;
      }
      
      // Refresh profile from database to ensure we have complete data
      await refreshProfile();
      
      toast.success("Avatar uploaded successfully");
      return result.url || null;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to upload avatar";
      toast.error(errorMessage);
      return null;
    }
  };

  const value: AuthContextType = {
    session,
    user,
    profile,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateProfile,
    uploadAvatar,
    loading,
    isAuthenticated: !!user,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook is now imported from @/hooks/useAuth
